import { useEffect, useRef, useState } from 'react';
import {
	Map as MapTilerMap,
	Marker,
	Popup,
	MapStyle,
	config as mapTilerConfig,
} from '@maptiler/sdk';
import { __ } from '@wordpress/i18n';
import { apiFetch } from '../scripts/api';

// Canary Islands, matching the default in locationPicker.js.
const DEFAULT_CENTER = [ -15.6, 28.3 ];
const DEFAULT_ZOOM = 7;

const MARKER_COLORS = {
	org: 'hsl(169 36% 15%)', // --color-green-dark
	space: 'hsl(44 100% 47%)', // --color-yellow
};

function escapeHtml( value ) {
	return String( value )
		.replace( /&/g, '&amp;' )
		.replace( /</g, '&lt;' )
		.replace( />/g, '&gt;' )
		.replace( /"/g, '&quot;' )
		.replace( /'/g, '&#039;' );
}

/**
 * Front-end directory map: shows every published org and space as a marker,
 * color-coded by type, with a popup linking to the record's page.
 */
export default function MapView() {
	const containerRef = useRef( null );
	const [ status, setStatus ] = useState( 'loading' ); // loading | ready | error | no-key
	const [ locationCount, setLocationCount ] = useState( 0 );

	useEffect( () => {
		const apiKey = window.anacrm?.mapTilerApiKey;
		if ( ! apiKey ) {
			setStatus( 'no-key' );
			return;
		}
		mapTilerConfig.apiKey = apiKey;

		const map = new MapTilerMap( {
			container: containerRef.current,
			style: MapStyle.STREETS,
			center: DEFAULT_CENTER,
			zoom: DEFAULT_ZOOM,
		} );

		let cancelled = false;

		apiFetch( '/anacrm/v1/map/locations' )
			.then( ( locations ) => {
				if ( cancelled ) {
					return;
				}
				locations.forEach( ( loc ) => {
					const popup = new Popup( { offset: 25 } ).setHTML(
						`<a href="${ escapeHtml( loc.url ) }">${ escapeHtml(
							loc.title
						) }</a>`
					);
					new Marker( { color: MARKER_COLORS[ loc.type ] } )
						.setLngLat( [ loc.lng, loc.lat ] )
						.setPopup( popup )
						.addTo( map );
				} );
				setLocationCount( locations.length );
				setStatus( 'ready' );
			} )
			.catch( () => {
				if ( ! cancelled ) {
					setStatus( 'error' );
				}
			} );

		return () => {
			cancelled = true;
			map.remove();
		};
	}, [] );

	if ( status === 'no-key' ) {
		return <p>{ __( 'Map is not configured.', 'anacrm' ) }</p>;
	}

	if ( status === 'error' ) {
		return <p>{ __( 'Could not load map locations.', 'anacrm' ) }</p>;
	}

	return (
		<div className="relative w-full h-full">
			{ status === 'loading' && (
				<p className="absolute z-10 top-2 left-2 bg-white px-2 py-1">
					{ __( 'Loading map…', 'anacrm' ) }
				</p>
			) }
			<div ref={ containerRef } className="w-full h-full" />
			{ status === 'ready' && locationCount === 0 && (
				<p className="absolute z-10 top-2 left-2 bg-white px-2 py-1">
					{ __( 'No locations to show yet.', 'anacrm' ) }
				</p>
			) }
		</div>
	);
}
